import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo } from 'react'

import type { MeResponse } from '@/lib/api'
import { ApiError, authApi } from '@/lib/api'
import { identifyUser, posthog, resetPostHog } from '@/lib/posthog'
import { arePermissionsReady } from '@/lib/permissions-ready'

interface User {
    id: string
    username: string
}

export interface ImpersonationState {
    actor: { id: string; username: string }
    target: { id: string; username: string }
}

interface AuthState {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
}

interface LoginResult {
    success: boolean
}

interface AuthContextValue extends AuthState {
    login: (data: {
        username: string
        password: string
    }) => Promise<LoginResult>
    logout: () => Promise<void>
    checkAuth: () => Promise<void>
    permissions: string[]
    // True once the /me query has settled, so consumers can tell an
    // intentionally empty permission set apart from "not loaded yet". login()
    // seeds permissions:[] synchronously then background-refetches, so a bare
    // permissions.length check would briefly look empty for a privileged user.
    arePermissionsLoaded: boolean
    hasPermission: (permission: string) => boolean
    hasAnyPermission: (...permissions: string[]) => boolean
    hasAllPermissions: (...permissions: string[]) => boolean
    // Non-null only while an admin is viewing as another user. `user`/`permissions`
    // above are the EFFECTIVE (target) identity; this exposes the actor too.
    impersonating: ImpersonationState | null
    isImpersonating: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)
export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const
const EMPTY_PERMISSIONS: string[] = []

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export interface MeQueryData {
    user: User | null
    permissions: string[]
    impersonating: ImpersonationState | null
}

// parseMeResponse maps the /auth/me payload to the cached auth state. The
// primary user is the EFFECTIVE user (the impersonated target while viewing as);
// the impersonating block carries both identities. Shared by the meQuery and the
// _authed beforeLoad so the two parses cannot drift.
export function parseMeResponse(data: MeResponse | undefined): MeQueryData {
    if (!data) return { user: null, permissions: [], impersonating: null }
    return {
        user: { id: data.user_id, username: data.username },
        permissions: (data.permissions as string[]) ?? [],
        impersonating: data.impersonating ?? null,
    }
}

// meQueryFn fetches and parses /auth/me, returning the empty (logged-out) state
// on any error. Shared by the provider's meQuery and the _authed beforeLoad so
// the two cannot diverge.
export async function meQueryFn(): Promise<MeQueryData> {
    try {
        const response = await authApi.me()
        return parseMeResponse(response.data)
    } catch (error) {
        // Only a genuine 401 means logged out → empty state. Transient/network
        // errors must NOT collapse to empty: that would flip isImpersonating off
        // and fire a spurious hard reset. Re-throw so React Query keeps the last
        // good data and surfaces isError instead.
        if (error instanceof ApiError && error.status === 401) {
            return { user: null, permissions: [], impersonating: null }
        }
        throw error
    }
}

export function useAuthProvider(): AuthContextValue {
    const queryClient = useQueryClient()

    const meQuery = useQuery({
        queryKey: AUTH_ME_QUERY_KEY,
        retry: false,
        queryFn: meQueryFn,
    })

    const loginMutation = useMutation({
        mutationFn: async (data: { username: string; password: string }) => {
            const response = await authApi.login(data)
            if (!response.data) {
                throw new Error('Login failed')
            }
            return response.data
        },
    })

    const logoutMutation = useMutation({
        mutationFn: async () => {
            await authApi.logout()
        },
        onSettled: () => {
            posthog.capture('user_logged_out')
            queryClient.clear()
            queryClient.setQueryData(AUTH_ME_QUERY_KEY, null)
            resetPostHog()
        },
    })

    const login = async (data: {
        username: string
        password: string
    }): Promise<LoginResult> => {
        const loginData = await loginMutation.mutateAsync(data)
        // Set user data immediately from login response to prevent
        // redirect loop between _app (React state) and login (cache check)
        queryClient.setQueryData<MeQueryData>(AUTH_ME_QUERY_KEY, {
            user: { id: loginData.user.id, username: loginData.user.username },
            permissions: [],
            impersonating: null,
        })
        // Background refresh to load full permissions
        void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
        return { success: true }
    }

    const logout = async () => {
        try {
            await logoutMutation.mutateAsync()
        } catch {
            // ignore logout errors
        }
    }

    const checkAuth = async () => {
        await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY })
    }

    const user = meQuery.data?.user ?? null
    const permissions = meQuery.data?.permissions ?? EMPTY_PERMISSIONS
    const impersonating = meQuery.data?.impersonating ?? null
    const isImpersonating = impersonating !== null
    const isAuthenticated = Boolean(user)
    const isLoading = meQuery.isLoading
    // Ready once we have real permissions, OR the fetch has settled with none.
    // Treating "have data, refreshing" as ready keeps a background refetch
    // (e.g. network reconnect) from unmounting the dashboard, while the
    // post-login empty+refetching window still reads as not-ready (no flash).
    const arePermissionsLoaded = arePermissionsReady({
        permissionCount: permissions.length,
        isFetched: meQuery.isFetched,
        isFetching: meQuery.isFetching,
    })

    // Analytics identity stays the ACTOR (admin) while impersonating, so the
    // target's identity never pollutes PostHog/Sentry.
    useEffect(() => {
        if (impersonating) {
            identifyUser(impersonating.actor)
        } else if (user) {
            identifyUser(user)
        }
    }, [user, impersonating])

    // useMemo required here — this is a context provider value.
    // React Compiler cannot stabilize context values automatically.
    return useMemo(() => {
        const permissionSet = new Set(permissions)
        return {
            user,
            isAuthenticated,
            isLoading,
            login,
            logout,
            checkAuth,
            permissions,
            arePermissionsLoaded,
            hasPermission: (permission: string) =>
                permissionSet.has(permission),
            hasAnyPermission: (...perms: string[]) =>
                perms.some((p) => permissionSet.has(p)),
            hasAllPermissions: (...perms: string[]) =>
                perms.every((p) => permissionSet.has(p)),
            impersonating,
            isImpersonating,
        }
    }, [
        user,
        isAuthenticated,
        isLoading,
        permissions,
        arePermissionsLoaded,
        impersonating,
        isImpersonating,
    ])
}

export { AuthContext }
